/**
 * useApp — the application context: the API adapter, the session, the global period, appearance
 * (theme / density), the collapsible sidebar, toasts, overlays, the refresh flow and the SSE
 * connection that invalidates the query cache.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Appearance, PeriodParam, SseEvent, Theme, User } from '../../../api-types.ts';
import { createHttpApi, createMockApi, errorMessage, shouldUseMock, type Api } from '../api-select.ts';
import { ALL_SCOPES, cacheClear, invalidate, type CacheScope } from './cache.ts';
import {
  Dialog,
  Drawer,
  Menu,
  Panel,
  Sheet,
  Toasts,
  type DialogSpec,
  type DrawerSpec,
  type LayerSpec,
  type MenuSpec,
  type PanelSpec,
  type SheetSpec,
  type ToastSpec,
} from '../components/overlays.tsx';
import { he } from '../i18n/he.ts';

const THEME_KEY = 'lt.theme';
const DENSITY_KEY = 'lt.density';
const PERIOD_KEY = 'lt.period';
const COLLAPSED_KEY = 'lt.collapsed';

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private mode — preferences simply do not persist */
  }
}

export interface AppValue {
  api: Api;
  user: User | null;
  setUser: (user: User | null) => void;
  appearance: Appearance;
  setAppearance: (patch: Partial<Appearance>) => void;
  period: PeriodParam;
  setPeriod: (period: PeriodParam) => void;
  collapsed: boolean;
  toggleCollapsed: () => void;
  toast: (message: string, options?: { error?: boolean; action?: { label: string; onClick: () => void } }) => void;
  openMenu: (anchor: HTMLElement, content: ReactNode, options?: { wide?: boolean; role?: 'menu' | 'dialog'; label?: string }) => void;
  openPanel: (spec: Omit<PanelSpec, 'kind'>) => void;
  openSheet: (spec: Omit<SheetSpec, 'kind'>) => void;
  openDrawer: (spec: Omit<DrawerSpec, 'kind'>) => void;
  openDialog: (spec: Omit<DialogSpec, 'kind'>) => void;
  closeLayer: () => void;
  /** True while a manual sync/refresh is running — screens dim to 50% with `aria-busy`. */
  syncing: boolean;
  startSync: (providerRefresh: boolean) => void;
}

const AppContext = createContext<AppValue | null>(null);

export function useApp(): AppValue {
  const value = useContext(AppContext);
  if (!value) throw new Error('AppProvider missing');
  return value;
}

export function AppProvider({ children, onUnauthenticated }: { children: ReactNode; onUnauthenticated: () => void }) {
  const apiRef = useRef<Api | null>(null);
  if (!apiRef.current) apiRef.current = shouldUseMock() ? createMockApi() : createHttpApi({ onUnauthenticated });
  const api = apiRef.current;

  const [user, setUser] = useState<User | null>(null);
  const [appearance, setAppearanceState] = useState<Appearance>(() => ({
    theme: (readLocal(THEME_KEY) as Theme | null) ?? 'auto',
    density: readLocal(DENSITY_KEY) === 'compact' ? 'compact' : 'normal',
    numberLocale: 'he-IL',
  }));
  const [period, setPeriodState] = useState<PeriodParam>(() => (readLocal(PERIOD_KEY) as PeriodParam | null) ?? 'month');
  const [collapsed, setCollapsed] = useState(() => readLocal(COLLAPSED_KEY) === '1');
  const [toasts, setToasts] = useState<ToastSpec[]>([]);
  const [layer, setLayer] = useState<LayerSpec | null>(null);
  const [syncing, setSyncing] = useState(false);
  const lastFocus = useRef<HTMLElement | null>(null);
  const toastSeq = useRef(0);

  // theme on <html> (tokens.css reads `data-theme`; "auto" removes the attribute)
  useEffect(() => {
    const root = document.documentElement;
    if (appearance.theme === 'auto') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', appearance.theme);
  }, [appearance.theme]);

  const setAppearance = useCallback((patch: Partial<Appearance>) => {
    setAppearanceState((prev) => {
      const next = { ...prev, ...patch };
      if (patch.theme) writeLocal(THEME_KEY, patch.theme);
      if (patch.density) writeLocal(DENSITY_KEY, patch.density);
      return next;
    });
  }, []);

  const setPeriod = useCallback((next: PeriodParam) => {
    writeLocal(PERIOD_KEY, next);
    setPeriodState(next);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      writeLocal(COLLAPSED_KEY, prev ? '0' : '1');
      return !prev;
    });
  }, []);

  const toast = useCallback((message: string, options?: { error?: boolean; action?: { label: string; onClick: () => void } }) => {
    toastSeq.current += 1;
    const id = toastSeq.current;
    setToasts((prev) => [...prev, { id, message, error: options?.error, action: options?.action }].slice(-2));
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), options?.action ? 6000 : 4000);
  }, []);

  const closeLayer = useCallback(() => {
    setLayer(null);
    const focus = lastFocus.current;
    lastFocus.current = null;
    if (focus?.isConnected) focus.focus({ preventScroll: true });
  }, []);

  const rememberFocus = () => {
    lastFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  };

  const openMenu = useCallback((anchor: HTMLElement, content: ReactNode, options?: { wide?: boolean; role?: 'menu' | 'dialog'; label?: string }) => {
    lastFocus.current = anchor;
    setLayer({ kind: 'menu', anchor: anchor.getBoundingClientRect(), content, wide: options?.wide, role: options?.role, label: options?.label });
  }, []);
  const openPanel = useCallback((spec: Omit<PanelSpec, 'kind'>) => {
    rememberFocus();
    setLayer({ kind: 'panel', ...spec });
  }, []);
  const openSheet = useCallback((spec: Omit<SheetSpec, 'kind'>) => {
    rememberFocus();
    setLayer({ kind: 'sheet', ...spec });
  }, []);
  const openDrawer = useCallback((spec: Omit<DrawerSpec, 'kind'>) => {
    rememberFocus();
    setLayer({ kind: 'drawer', ...spec });
  }, []);
  const openDialog = useCallback((spec: Omit<DialogSpec, 'kind'>) => {
    rememberFocus();
    setLayer({ kind: 'dialog', ...spec });
  }, []);

  // SSE — invalidate the query cache and surface sync toasts
  useEffect(() => {
    if (!user) return undefined;
    return api.subscribe((event: SseEvent) => {
      switch (event.event) {
        case 'sync:start':
          setSyncing(true);
          break;
        case 'sync:done':
          setSyncing(false);
          invalidate(ALL_SCOPES);
          toast(he.refresh.ok);
          break;
        case 'sync:error':
          setSyncing(false);
          toast(event.data.message || he.refresh.failed, { error: true });
          break;
        case 'alerts:changed':
          invalidate(['summary']);
          break;
        case 'data:changed':
          invalidate(event.data.scopes as CacheScope[]);
          break;
        default:
          break;
      }
    });
  }, [api, user, toast]);

  const startSync = useCallback(
    (providerRefresh: boolean) => {
      setSyncing(true);
      api.sync(providerRefresh).then(
        () => {
          /* the spinner + the 50% busy state already say it started; the completion toast
             arrives over SSE as `sync:done` (spec §3.4) */
        },
        (error: unknown) => {
          setSyncing(false);
          toast(errorMessage(error), { error: true });
        },
      );
    },
    [api, toast],
  );

  const value = useMemo<AppValue>(
    () => ({
      api,
      user,
      setUser: (next) => {
        if (!next) cacheClear();
        setUser(next);
      },
      appearance,
      setAppearance,
      period,
      setPeriod,
      collapsed,
      toggleCollapsed,
      toast,
      openMenu,
      openPanel,
      openSheet,
      openDrawer,
      openDialog,
      closeLayer,
      syncing,
      startSync,
    }),
    [api, user, appearance, setAppearance, period, setPeriod, collapsed, toggleCollapsed, toast, openMenu, openPanel, openSheet, openDrawer, openDialog, closeLayer, syncing, startSync],
  );

  return (
    <AppContext.Provider value={value}>
      {children}
      {layer?.kind === 'menu' ? <Menu spec={layer} onClose={closeLayer} /> : null}
      {layer?.kind === 'panel' ? <Panel spec={layer} onClose={closeLayer} /> : null}
      {layer?.kind === 'sheet' ? <Sheet spec={layer} onClose={closeLayer} /> : null}
      {layer?.kind === 'drawer' ? <Drawer spec={layer} onClose={closeLayer} /> : null}
      {layer?.kind === 'dialog' ? <Dialog spec={layer} onClose={closeLayer} /> : null}
      <Toasts items={toasts} onDismiss={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))} />
    </AppContext.Provider>
  );
}
